import { FC, useMemo } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, FormInputText, InputLabel, Modal, Select, Text } from '@hopskipdrive/react-components';
import { includes } from 'lodash';
import pluralize from 'pluralize';

import { Button } from '../../../shared/components/Button';
import { PhotoOrInitials } from '../../../shared/components/PhotoOrInitials';
import { colorPalette, colorTokens } from '../../../shared/components/variables';
import { useToast } from '../../../shared/contexts/ToastContext';
import { RIDE_STATES } from '../../constants/rideStates';
import { useCancelRideSubmit } from '../../hooks/useCancelRideSubmit';
import { CancellationReason, RiderOnRide } from '../../types/rides';
import { OutletContextType } from '../PageHeading';

import { cancelRideValidationSchema } from './cancelRideValidationSchema';

interface FormData {
  tripCancellation: {
    adminCancellationReason: string;
    requestedBy: string;
    notes: string;
  };
}

const defaultRideCancel = {
  tripCancellation: {
    adminCancellationReason: '',
    requestedBy: '',
    notes: '',
  },
};

function useCancelRide() {
  return useOutletContext<OutletContextType>();
}

export const CancelRideModal = () => {
  const { rideSlug, ridersOnRide, cancelReasons, rideState } = useCancelRide();
  const canCancel = !includes([...RIDE_STATES.completed, ...RIDE_STATES.canceled], rideState);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { mutate, isPending } = useCancelRideSubmit({
    rideSlug: rideSlug,
    successCallback: () => {
      addToast({
        severity: 'success',
        message: 'Ride Canceled',
      });
      navigate('..');
    },
    errorCallback: (
      error: Error & {
        errors: string[];
      }
    ) => {
      error?.errors
        ? error?.errors?.forEach((errorMessage) => {
            addToast({
              severity: 'error',
              message: errorMessage,
            });
          })
        : addToast({
            severity: 'error',
            message: error.message,
          });
    },
  });
  const hasMultipleRiders = ridersOnRide?.length > 1;
  const methods = useForm<FormData>({
    defaultValues: useMemo(() => {
      return defaultRideCancel;
    }, []),
    resolver: yupResolver(cancelRideValidationSchema()),
  });

  return (
    <Modal open={canCancel} maxWidth="md" data-testid="cancel-ride-modal">
      <Modal.Title>Cancel this ride for:</Modal.Title>
      <Modal.Content>
        <FormProvider {...methods}>
          <RiderRemovalsForm reasons={cancelReasons} ridersToRemove={ridersOnRide} handleSubmit={mutate} />
        </FormProvider>
        {hasMultipleRiders && (
          <Alert
            severity="info"
            variant="standard"
            data-testid="rebuild-ride-info"
            title="Don’t need to cancel for all Riders?"
            message=""
            style={{ marginTop: '12px' }}
            action={
              <Button
                color="tonal"
                variant="text"
                style={{ border: `1px solid ${colorTokens.primary.outlineBorder}` }}
                onClick={() => navigate(`/rides/${rideSlug}/edit`)}
              >
                Edit Ride
              </Button>
            }
            onClose={() => {}}
          />
        )}
      </Modal.Content>
      <Modal.Actions style={{ borderTop: `1px solid ${colorPalette.neutralVariant90}` }}>
        <Button variant="text" onClick={() => navigate('..')}>
          Never mind
        </Button>
        <Button type="submit" color="primary" form="cancel-ride-form" loading={isPending}>
          {isPending ? 'Canceling...' : `Cancel ${hasMultipleRiders ? 'for All Riders' : 'Ride'}`}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

interface RiderRemovalsFormProps {
  ridersToRemove: RiderOnRide[];
  handleSubmit: (formData: FormData) => void;
  reasons: CancellationReason[];
}

const RiderRemovalsForm: FC<RiderRemovalsFormProps> = ({ ridersToRemove, handleSubmit, reasons }) => {
  const methods = useFormContext<FormData>();
  const {
    control,
    formState: { errors },
  } = methods;

  return (
    <form
      id="cancel-ride-form"
      onSubmit={methods.handleSubmit(handleSubmit)}
      style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
    >
      <Box display="flex" flexDirection="row" alignItems="center">
        {ridersToRemove?.map((rider) => (
          <PhotoOrInitials
            key={`cancelRide-rider-${rider.id}`}
            fullName={`${rider.firstName} ${rider.lastName}`}
            pictureUrl={rider.profilePictureUrl}
            size={33}
            fontSize={14}
          />
        ))}
      </Box>
      <Text variant="h6">{`${pluralize('Rider', ridersToRemove?.length)}: ${ridersToRemove
        ?.map((rider) => {
          return `${rider.firstName} ${rider.lastName}`;
        })
        .join(', ')}`}</Text>
      <Box display="flex" gap="6px" flexDirection="column" mt="6px">
        <FormFieldContent>
          <InputLabel fieldtype="required" htmlFor="cancalReason">
            Reason
          </InputLabel>
          <Controller
            control={control}
            name={'tripCancellation.adminCancellationReason'}
            render={({ field: { value, onChange } }) => (
              <Select
                options={reasons || []}
                value={value}
                setValue={onChange}
                style={{ width: '323px' }}
                renderInput={(params) => (
                  <FormInputText
                    {...params}
                    variant="outlined"
                    data-testid="removalReason"
                    placeholder="Select"
                    error={!!errors.tripCancellation?.adminCancellationReason}
                    helperText={
                      errors?.tripCancellation?.adminCancellationReason &&
                      errors?.tripCancellation.adminCancellationReason?.message
                    }
                  />
                )}
              />
            )}
          />
        </FormFieldContent>
        <FormFieldContent>
          <InputLabel fieldtype="required" htmlFor="requestedBy">
            Requested by
          </InputLabel>
          <Controller
            control={control}
            name={'tripCancellation.requestedBy'}
            render={({ field: { value, onChange } }) => (
              <FormInputText
                value={value}
                onChange={onChange}
                variant="outlined"
                data-testid="requestedBy"
                style={{ width: '323px' }}
                placeholder="Enter name"
                error={!!errors?.tripCancellation?.requestedBy}
                helperText={errors?.tripCancellation?.requestedBy && errors?.tripCancellation?.requestedBy?.message}
              />
            )}
          />
        </FormFieldContent>
        <FormFieldContent>
          <InputLabel htmlFor="notes">Note</InputLabel>
          <Controller
            control={control}
            name={'tripCancellation.notes'}
            render={({ field: { value, onChange } }) => (
              <FormInputText
                value={value}
                onChange={onChange}
                variant="outlined"
                data-testid="notes"
                style={{ width: '323px' }}
                placeholder="Enter any additional info"
              />
            )}
          />
        </FormFieldContent>
      </Box>
    </form>
  );
};

const FormFieldContent = ({ children }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between">
    {children}
  </Box>
);
